import { useMemo, useState } from 'react';
import { useAtualizarReceita, useCriarReceita, usePrefillProcesso } from '../../hooks';
import type { FormaRecebimento, Receita, ReceitaIn, Socio, StatusReceita } from '../../types';
import { fmtBRL } from './formatadores';
import { DivisaoSociosEditor, defaultDivisao, divisaoValida, type ItemDivisao } from './DivisaoSociosEditor';

interface Props {
  receita?: Receita | null;   // null/undefined = criar
  socios: Socio[];
  pctPadrao: number;          // % contratuais default (config do escritório)
  onClose: () => void;
}

const FORMAS: { v: FormaRecebimento; t: string }[] = [
  { v: 'pix', t: 'PIX' }, { v: 'ted', t: 'TED' }, { v: 'deposito', t: 'Depósito' },
  { v: 'dinheiro', t: 'Dinheiro' }, { v: 'cheque', t: 'Cheque' }, { v: 'outro', t: 'Outro' },
];

function divisaoDeReceita(r: Receita | null | undefined, socios: Socio[]): ItemDivisao[] {
  if (r?.divisao?.length) {
    return r.divisao.map((d) => ({
      socio_id: d.socio_id,
      socio_nome: d.socio_nome ?? socios.find((s) => s.id === d.socio_id)?.nome ?? '?',
      pct_contratuais: d.pct_contratuais,
      pct_sucumbencia: d.pct_sucumbencia,
    }));
  }
  return defaultDivisao(socios);
}

export function FormReceita({ receita, socios, pctPadrao, onClose }: Props) {
  const editar = !!receita;
  const [numero, setNumero] = useState(receita?.numero_processo ?? '');
  const [cliente, setCliente] = useState(receita?.cliente_nome ?? '');
  const [advogado, setAdvogado] = useState(receita?.advogado_responsavel ?? '');
  const [valorBase, setValorBase] = useState<number>(receita?.valor_base ?? 0);
  const [pctContr, setPctContr] = useState<number>(receita?.pct_contratuais ?? pctPadrao);
  const [valorSucumb, setValorSucumb] = useState<number>(receita?.valor_sucumbencia ?? 0);
  const [status, setStatus] = useState<StatusReceita>(receita?.status ?? 'previsto');
  const [valorRecebido, setValorRecebido] = useState<number>(receita?.valor_recebido ?? 0);
  const [dataPrevista, setDataPrevista] = useState(receita?.data_prevista ?? '');
  const [dataRecebimento, setDataRecebimento] = useState(receita?.data_recebimento ?? '');
  const [forma, setForma] = useState<FormaRecebimento | ''>(receita?.forma_recebimento ?? '');
  const [obs, setObs] = useState(receita?.observacao ?? '');
  const [divisao, setDivisao] = useState<ItemDivisao[]>(() => divisaoDeReceita(receita, socios));
  const [avisoProcesso, setAvisoProcesso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const criar = useCriarReceita();
  const atualizar = useAtualizarReceita();
  const prefill = usePrefillProcesso();
  const salvando = criar.isPending || atualizar.isPending;

  const valorContr = useMemo(() => Math.round(valorBase * pctContr) / 100, [valorBase, pctContr]);
  const valorTotal = useMemo(() => Math.round((valorContr + valorSucumb) * 100) / 100, [valorContr, valorSucumb]);
  const divisaoOk = divisaoValida(divisao);
  const podeSalvar = numero.trim().length >= 5 && divisaoOk && !salvando;

  async function aoSairDoNumero() {
    const raw = numero.replace(/\D/g, '');
    if (raw.length < 11 || editar) return;
    try {
      const p = await prefill.mutateAsync(numero.trim());
      if (p.cliente_nome && !cliente.trim()) setCliente(p.cliente_nome);
      setAvisoProcesso(p.ja_tem_receita ? 'Já existe receita para este processo.' : null);
    } catch { /* prefill é best-effort */ }
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    if (!podeSalvar) return;
    setErro(null);
    const body: ReceitaIn = {
      numero_processo: numero.trim(),
      cliente_nome: cliente.trim() || null,
      advogado_responsavel: advogado.trim() || null,
      valor_base: valorBase,
      pct_contratuais: pctContr,
      valor_sucumbencia: valorSucumb,
      status,
      valor_recebido: status === 'recebido' ? (valorRecebido || valorTotal) : valorRecebido,
      data_prevista: dataPrevista || null,
      data_recebimento: status === 'previsto' ? null : (dataRecebimento || null),
      forma_recebimento: forma || null,
      observacao: obs.trim() || null,
      divisao: divisao.map((d) => ({ socio_id: d.socio_id, pct_contratuais: d.pct_contratuais, pct_sucumbencia: d.pct_sucumbencia })),
    };
    try {
      if (editar && receita) await atualizar.mutateAsync({ id: receita.id, body });
      else await criar.mutateAsync(body);
      onClose();
    } catch (err: any) {
      if (err?.status === 403) setErro('Sem permissão.');
      else setErro(err?.message ?? 'Erro ao salvar a receita.');
    }
  }

  const lbl = 'block text-xs text-gray-500 dark:text-gray-400 mb-1';
  const inp = 'w-full text-sm border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
            {editar ? '✎ Editar receita' : '➕ Nova receita por processo'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none" aria-label="Fechar">×</button>
        </div>

        <form onSubmit={salvar} className="space-y-3">
          <div>
            <label className={lbl}>Número do processo *</label>
            <input value={numero} onChange={(e) => setNumero(e.target.value)} onBlur={aoSairDoNumero}
              placeholder="0000000-00.0000.5.01.0000" className={inp}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={!editar} disabled={editar} />
            {avisoProcesso && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">{avisoProcesso}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Cliente</label>
              <input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="(prefill pelo nº)" className={inp} />
            </div>
            <div>
              <label className={lbl}>Advogado responsável</label>
              <input value={advogado} onChange={(e) => setAdvogado(e.target.value)} className={inp} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={lbl}>Valor-base (R$)</label>
              <input type="number" min={0} step="0.01" value={valorBase} onChange={(e) => setValorBase(parseFloat(e.target.value) || 0)} className={`${inp} text-right tabular-nums`} />
            </div>
            <div>
              <label className={lbl}>% contratuais</label>
              <input type="number" min={0} max={100} step="0.01" value={pctContr} onChange={(e) => setPctContr(parseFloat(e.target.value) || 0)} className={`${inp} text-right tabular-nums`} />
            </div>
            <div>
              <label className={lbl}>Sucumbência (R$)</label>
              <input type="number" min={0} step="0.01" value={valorSucumb} onChange={(e) => setValorSucumb(parseFloat(e.target.value) || 0)} className={`${inp} text-right tabular-nums`} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 px-3 py-2">
            <span className="text-slate-500 dark:text-slate-400">Contratuais: <strong className="text-slate-700 dark:text-slate-200 tabular-nums">{fmtBRL(valorContr)}</strong></span>
            <span className="text-slate-500 dark:text-slate-400">Sucumbência: <strong className="text-slate-700 dark:text-slate-200 tabular-nums">{fmtBRL(valorSucumb)}</strong></span>
            <span className="ml-auto text-sm text-blue-700 dark:text-blue-300 font-semibold tabular-nums">Total: {fmtBRL(valorTotal)}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className={lbl}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as StatusReceita)} className={inp}>
                <option value="previsto">Previsto</option>
                <option value="parcial">Parcial</option>
                <option value="recebido">Recebido</option>
              </select>
            </div>
            <div>
              <label className={lbl}>Data prevista</label>
              <input type="date" value={dataPrevista ?? ''} onChange={(e) => setDataPrevista(e.target.value)} className={inp} />
            </div>
            {status !== 'previsto' && (
              <>
                <div>
                  <label className={lbl}>Recebido (R$)</label>
                  <input type="number" min={0} step="0.01" value={valorRecebido} onChange={(e) => setValorRecebido(parseFloat(e.target.value) || 0)} className={`${inp} text-right tabular-nums`} />
                </div>
                <div>
                  <label className={lbl}>Data do recebimento</label>
                  <input type="date" value={dataRecebimento ?? ''} onChange={(e) => setDataRecebimento(e.target.value)} className={inp} />
                </div>
              </>
            )}
          </div>

          {status !== 'previsto' && (
            <div className="w-40">
              <label className={lbl}>Forma</label>
              <select value={forma} onChange={(e) => setForma(e.target.value as FormaRecebimento | '')} className={inp}>
                <option value="">—</option>
                {FORMAS.map((f) => <option key={f.v} value={f.v}>{f.t}</option>)}
              </select>
            </div>
          )}

          <DivisaoSociosEditor socios={socios} itens={divisao} valorContratuais={valorContr} valorSucumbencia={valorSucumb} onChange={setDivisao} />

          <div>
            <label className={lbl}>Observação</label>
            <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} className={inp} />
          </div>

          {erro && <p className="text-xs text-rose-600 dark:text-rose-400">{erro}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
            <button type="submit" disabled={!podeSalvar}
              className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
              {salvando ? 'Salvando…' : editar ? 'Salvar' : 'Criar receita'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
